/**
 * The peer's device list, kept locally and trusted because it is signed.
 *
 * Fail-closed only works if "closed" does not also mean "offline". A check
 * that refuses every message whenever the network hiccups is a check somebody
 * turns off, and then it protects nothing at all.
 *
 * A signed list makes that easy in a way an unsigned answer never could: the
 * signature travels with the data, so a list verified once can be kept and
 * re-verified later without asking anyone. The server is needed to learn about
 * a NEW version — never to confirm the one we already hold.
 *
 * So the rule is: refuse a device that is not in the list we hold; refuse
 * everything only if we have never held one. That is genuinely closed — a
 * server cannot induce acceptance by going quiet, it can only stop us learning
 * about devices the person legitimately added, which is a visible failure
 * rather than a silent one.
 */

import { verifyList, listAuthorises, LIST_ERROR, verifyChain, listHash } from './deviceList.mjs';

const key = (userId) => `devicelist:${userId}`;
const masterKey = (userId) => `mastermin:${userId}`;

/**
 * The account key we believe belongs to this person.
 *
 * This is the pin that matters now, and it is a different object from the one
 * the calls hold. Calls pin a DEVICE identity, which is why every new phone
 * used to be an event somebody had to adjudicate. Pinning the ACCOUNT master
 * instead lets devices come and go underneath a key that does not change —
 * which is the whole reason Signal's safety number survives linking a laptop.
 *
 * Trust on first use, as everywhere else here, and worth saying plainly: the
 * first master we ever see for somebody is the one we believe. What changes
 * is that there is now exactly one such moment per person instead of one per
 * device they ever own.
 */
export async function peerMaster(store, userId) {
  return (await store.getValue(masterKey(userId))) || null;
}

export async function pinPeerMaster(store, userId, key64) {
  const held = await peerMaster(store, userId);
  if (held) return { ok: held.key === key64, held, changed: held.key !== key64 };
  await store.setValue(masterKey(userId), { key: key64, pinnedAt: 0 });
  return { ok: true, firstUse: true };
}

/** What we hold for this account: the verified list, its version and hash. */
export async function heldList(store, userId) {
  return (await store.getValue(key(userId))) || null;
}

/**
 * Fetch, verify, and keep — or keep what we had.
 *
 * `pinnedMaster` is the account master this device trusts for that person. A
 * list signed by anything else is not a newer list, it is a different account
 * wearing the same name, and it must never replace what we hold.
 */
export async function refreshList(store, api, userId, pinnedMaster) {
  const held = await heldList(store, userId);
  if (!pinnedMaster) return { held, refreshed: false, why: 'no pinned master' };

  let published;
  try {
    // Ask for everything after the version we hold, so a jump comes with
    // the steps that make it a history.
    published = await api.getDeviceList(userId, held ? held.version : null);
  } catch (e) {
    return { held, refreshed: false, why: 'unreachable' };
  }
  if (!published || !published.published) return { held, refreshed: false, why: 'none published' };
  if (published.masterKey !== pinnedMaster) {
    return { held, refreshed: false, why: 'signed by another master', foreign: true };
  }

  const seen = held ? { version: held.version, hash: held.hash } : null;
  let verdict = verifyList({ list: published.list, sig: published.sig }, pinnedMaster, seen);
  if (!verdict.ok && verdict.error === LIST_ERROR.GAP) {
    // More than one step ahead: walk the chain the server sent. No chain, a
    // chain with a hole, or one that ends somewhere else — the jump stays a
    // claim, and we keep what we had.
    const walked = verifyChain(published.chain, pinnedMaster, seen);
    verdict = walked.ok && walked.hash === listHash(published.list) ? walked
      : (walked.ok ? { ok: false, error: LIST_ERROR.GAP, seen } : walked);
  }
  if (!verdict.ok) {
    // A rollback or a fork is news about the server, not about the peer. Keep
    // what we had — adopting the newer-looking thing is exactly what an
    // attacker is hoping for.
    return { held, refreshed: false, why: verdict.error, alarm: verdict.error };
  }
  // The same list again is not a refresh: nothing changed, so nothing that
  // waits on a change may be woken by it (each wake of a held message costs a
  // one-time prekey of the device it is held on).
  if (held && held.version === verdict.version && held.hash === verdict.hash) {
    return { held, refreshed: false, why: 'same' };
  }
  const next = { version: verdict.version, hash: verdict.hash, devices: verdict.devices,
                 list: published.list, sig: published.sig };
  await store.setValue(key(userId), next);
  return { held: next, refreshed: true };
}

export const DEVICE_VERDICT = {
  OK: 'ok',
  NO_LIST: 'no-list',           // we have never held a list for this account
  NOT_LISTED: 'not-listed',     // the account has not authorised this device
  WRONG_KEYS: 'wrong-keys',     // right device id, keys the list does not name
};

/**
 * Is this device one the account authorised?
 *
 * `identitySig` is checked as well as the id, because a device id the account
 * signed for once must not become a slot the server can refill with different
 * keys.
 */
export function checkDevice(held, deviceId, identitySig = null) {
  if (!held || !held.list) return DEVICE_VERDICT.NO_LIST;
  const d = held.list.devices.find(x => x.deviceId === deviceId);
  if (!d) return DEVICE_VERDICT.NOT_LISTED;
  if (identitySig && d.identitySig !== identitySig) return DEVICE_VERDICT.WRONG_KEYS;
  return DEVICE_VERDICT.OK;
}

export function verdictReason(verdict, label) {
  // "у это устройство ключи" — the fallback was written as a noun and used
  // where a genitive was needed. Nobody reads their own error strings until a
  // log does it for them.
  const who = label ? `«${label}»` : 'этого устройства';
  switch (verdict) {
    case DEVICE_VERDICT.NO_LIST:
      return 'у собеседника ещё нет подписанного списка устройств — писать некому, пока он не появится';
    case DEVICE_VERDICT.NOT_LISTED:
      return `${label ? `«${label}»` : 'это устройство'} не входит в список устройств, который подписал сам аккаунт`;
    case DEVICE_VERDICT.WRONG_KEYS:
      return `у ${who} ключи, которых нет в подписанном списке`;   // "у этого устройства ключи…"
    default:
      return '';
  }
}

/**
 * Forget what we hold about an account's own key — a deliberate act, never a
 * side effect.
 *
 * The master pin is the thing that makes a changed account key MEAN something:
 * without it the server could swap the list under a name and nobody would
 * notice. So the pin must never be dropped quietly. But the model says «a
 * person decides», and until this existed there was no way for the person to
 * decide anything: an account that started over — every device lost, a new
 * master minted, which the server records as a master-change event — was
 * refused for ever, and the screen said «wait until they confirm on their
 * side» about something they had already done (03.09.2026).
 *
 * Dropping the pin puts this account back to first sight: the next list is
 * pinned as it would be for a stranger. It touches nothing else — sessions,
 * message keys and the ratchet do not depend on it — and the change stays in
 * the server's key log, which peers gossip about.
 */
export async function forgetAccountKey(store, userId) {
  await store.setValue(masterKey(userId), null);
  await store.setValue(key(userId), null);
}

export { key as deviceListKey };

/**
 * Learn about a person's account for the first time, or check what we know.
 *
 * Folded into one call because the two cases differ only in whether a pin
 * already exists, and splitting them is how one of them ends up without a
 * check. A master that has CHANGED is not a refresh — it is a different
 * account answering to the same name, and it stops here.
 */
export async function syncPeer(store, api, userId) {
  let published;
  try {
    published = await api.getDeviceList(userId);
  } catch (e) {
    return { held: await heldList(store, userId), refreshed: false, why: 'unreachable' };
  }
  if (!published || !published.published) {
    return { held: await heldList(store, userId), refreshed: false, why: 'none published' };
  }

  const pin = await pinPeerMaster(store, userId, published.masterKey);
  if (!pin.ok) {
    return { held: await heldList(store, userId), refreshed: false,
             why: 'master changed', masterChanged: true, was: pin.held.key, now: published.masterKey };
  }
  return { ...(await refreshList(store, api, userId, published.masterKey)), firstUse: !!pin.firstUse };
}

/**
 * The gate the trust layer asks, in the shape it expects.
 *
 * One function so that "may I talk to this device" has a single answer used by
 * sending and by receiving. Two answers is how the two sides of a session end
 * up disagreeing about who is allowed in it, which is exactly the class of bug
 * that let an unauthenticated handshake through in the first place.
 *
 * `strict` is what makes it fail-closed. With it off, an account we have never
 * synced with is allowed through — the state every account is in on the day
 * this ships, and the state none of them should stay in.
 */
export function makeDeviceListGate(store, api, { strict = true, onUnlisted = null, onRefreshed = null } = {}) {
  return async (peerUserId, deviceId, identitySig) => {
    let held = await heldList(store, peerUserId);
    if (!held || !checkDeviceIn(held, deviceId, identitySig)) {
      // Only ask the server when the answer we hold says no: a device we
      // already know about must never cost a request, and a server that wants
      // to be asked repeatedly should not be able to arrange it.
      const r = await syncPeer(store, api, peerUserId);
      held = r.held;
      // Say what the sync did: a refusal here is otherwise indistinguishable
      // from a peer who simply never authorised the device.
      console.log(`[CHAT] device-list sync for user ${peerUserId}: ${r.refreshed ? 'refreshed' : 'kept'}${r.why ? ' (' + r.why + ')' : ''}, held v${held && held.version}${r.alarm ? ' ALARM ' + r.alarm : ''}`);
      // A new list is the event a held message may have been waiting for: the
      // account just vouched for a device nobody had confirmed by hand — and
      // for one's own account nobody ever could (no chat with oneself to
      // press Confirm in). Without this the held job sat six hours at the
      // head of its queue and every later message waited behind it in
      // silence (iPhone, 02.09.2026).
      if (r.refreshed && onRefreshed) { try { onRefreshed({ userId: Number(peerUserId), version: held && held.version }); } catch (e) { /* the gate's answer does not depend on it */ } }
      if (r.masterChanged) {
        const reason = 'аккаунт собеседника подписан другим мастер-ключом, чем раньше';
        if (onUnlisted) onUnlisted({ userId: Number(peerUserId), deviceId, reason, masterChanged: true });
        // The refusal says WHY, so the screen can offer the one action that
        // fits it — forgetting the old account key — instead of «wait until
        // they confirm on their side» about something already done.
        return { ok: false, reason, masterChanged: true };
      }
    }

    const verdict = checkDevice(held, deviceId, identitySig);
    if (verdict === DEVICE_VERDICT.OK) return { ok: true };
    if (verdict === DEVICE_VERDICT.NO_LIST && !strict) return { ok: true, unchecked: true };

    const reason = verdictReason(verdict);
    if (onUnlisted) onUnlisted({ userId: Number(peerUserId), deviceId, reason, verdict });
    return { ok: false, reason, verdict };
  };
}

function checkDeviceIn(held, deviceId, identitySig) {
  return checkDevice(held, deviceId, identitySig) === DEVICE_VERDICT.OK;
}
